/**
 * M1 나 (DEV_SPEC 4.8, 시안 03 README M1).
 *
 *   '나' → 이름 카드 → 내 사주 정보 + 수정하기 → 알림 받기 6개 → 멤버(얼굴 다섯 + 「멤버 소개 ›」)
 *   → 이모 수첩 ›(시안 04 D2c, 옛 '기억') → 계정(로그인 방식 · 로그아웃 · 회원 탈퇴, 시안 06) → 고지 → 하단 탭
 *
 * - 사주 정보는 첫 방문에서 묻지 않으므로 없을 수 있습니다(D14). 없으면 「생일 알려주기」
 * - 알림 기본값: 운세·한 문장·체크인·고민 후속·별채 부르기 켜짐, 오늘의 한 수 꺼짐 (config/notifications)
 * - 이모 수첩은 멤버들이 유저가 **직접 말한 것만** 담습니다. 보고 고치고 지우는 건 수첩 화면(/me/memo)에서
 * - 「도움받을 곳」 섹션은 두지 않습니다(시안 03 10장 9, 사용자 결정 2026-10-12). 109 · 1577-0199 는
 *   위기 대화의 안전 카드와 「도움받을 곳 다시 보기」 알약에서 보입니다
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, FileText, NotebookPen } from 'lucide-react';
import { MEMBER_KEYS } from '@config/members/index.js';
import { NOTIFICATION_KINDS } from '@config/notifications/index.js';
import { Card } from '../../components/Card.jsx';
import { Button } from '../../components/Button.jsx';
import { Switch } from '../../components/Switch.jsx';
import { AvatarStack } from '../../components/Avatar.jsx';
import { errorMessageOf } from '../../api/client.js';
import * as meApi from '../../api/me.js';
import { useSession } from '../../stores/session.js';
import { BirthSheet } from '../../components/birth/BirthSheet.jsx';
import { formatBirthDate, formatBirthTime } from './meUtils.js';
import { AccountSection } from './AccountSection.jsx';
import { NameEditor } from './NameEditor.jsx';
import { SajuDelete } from './SajuDelete.jsx';

const DEFAULT_NOTIFICATIONS = NOTIFICATION_KINDS.map(({ kind, name, defaultOn }) => ({
    kind,
    name,
    enabled: defaultOn,
}));

export function MePage() {
    const navigate = useNavigate();
    const user = useSession((state) => state.user);
    const logout = useSession((state) => state.logout);

    const [saju, setSaju] = useState(null);
    const [sajuLoaded, setSajuLoaded] = useState(false);
    const [birthSheetOpen, setBirthSheetOpen] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [account, setAccount] = useState(null);
    const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATIONS);
    const [notifyError, setNotifyError] = useState(null);
    // 이모가 사주 얘기 곁들이기 (기본 켬, C-17). 생일을 알려 준 단골에게만 보입니다
    const [sajuAside, setSajuAside] = useState(true);
    // 내가 없을 때 이어지는 얘기(여운 · 늦게 온 멤버, 기본 켬, C-20b · D67)
    const [roomAfterglow, setRoomAfterglow] = useState(true);
    // 멤버가 먼저 이야기 꺼내기(대화가 멈췄을 때 화제 하나, 기본 켬, C-25 · D68)
    const [topicNudge, setTopicNudge] = useState(true);
    const [prefError, setPrefError] = useState(null);
    const [roomPrefError, setRoomPrefError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        meApi
            .fetchMySaju({ signal })
            .then((response) => {
                setSaju(response.saju);
                setSajuLoaded(true);
            })
            .catch((error) => {
                if (!signal.aborted) {
                    setLoadError(errorMessageOf(error, '사주 정보를 불러오지 못했어요.'));
                }
            });
        meApi
            .fetchPrefs({ signal })
            .then((response) => {
                setSajuAside(response.prefs.sajuAside);
                setRoomAfterglow(response.prefs.roomAfterglow ?? true);
                setTopicNudge(response.prefs.topicNudge ?? true);
            })
            .catch(() => {
                // 기본값(켬)을 보여 줍니다
            });
        meApi
            .fetchAccount({ signal })
            .then((response) => setAccount(response.account))
            .catch(() => {
                // 이메일 줄로 보여 줍니다
            });
        meApi
            .fetchNotifications({ signal })
            .then((response) => setNotifications(response.notifications))
            .catch(() => {
                // 기본값을 보여 줍니다. 바꾸려고 누르면 그때 오류를 알립니다
            });
        return () => {
            controller.abort();
        };
    }, []);

    async function toggleNotification(kind, enabled) {
        const previous = notifications;
        setNotifyError(null);
        setNotifications((items) => items.map((item) => (item.kind === kind ? { ...item, enabled } : item)));
        try {
            const response = await meApi.updateNotification(kind, enabled);
            setNotifications(response.notifications);
        } catch (error) {
            setNotifications(previous);
            setNotifyError(errorMessageOf(error, '알림을 바꾸지 못했어요. 잠시 후 다시 시도해 주세요.'));
        }
    }

    async function toggleSajuAside(enabled) {
        setPrefError(null);
        setSajuAside(enabled);
        try {
            const response = await meApi.updatePrefs({ sajuAside: enabled });
            setSajuAside(response.prefs.sajuAside);
        } catch (error) {
            setSajuAside(!enabled);
            setPrefError(errorMessageOf(error, '설정을 바꾸지 못했어요. 잠시 후 다시 시도해 주세요.'));
        }
    }

    async function toggleTopicNudge(enabled) {
        setRoomPrefError(null);
        setTopicNudge(enabled);
        try {
            const response = await meApi.updatePrefs({ topicNudge: enabled });
            setTopicNudge(response.prefs.topicNudge);
        } catch (error) {
            setTopicNudge(!enabled);
            setRoomPrefError(errorMessageOf(error, '설정을 바꾸지 못했어요. 잠시 후 다시 시도해 주세요.'));
        }
    }

    async function toggleRoomAfterglow(enabled) {
        setRoomPrefError(null);
        setRoomAfterglow(enabled);
        try {
            const response = await meApi.updatePrefs({ roomAfterglow: enabled });
            setRoomAfterglow(response.prefs.roomAfterglow);
        } catch (error) {
            setRoomAfterglow(!enabled);
            setRoomPrefError(errorMessageOf(error, '설정을 바꾸지 못했어요. 잠시 후 다시 시도해 주세요.'));
        }
    }

    // M2 확인 → 세션 지움 → 시작 화면(교체 이동, 뒤로 불가)
    async function handleLogout() {
        await logout();
        navigate('/start', { replace: true });
    }

    const displayName = user?.displayName ?? '';

    return (
        <>
            <header className="shrink-0 px-gutter pt-5 pb-3">
                <h1 className="m-0 text-title-1">나</h1>
            </header>
            <main className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-gutter pb-6">
                <Card className="flex items-center gap-3">
                    <span
                        aria-hidden="true"
                        className="flex size-12 items-center justify-center rounded-full bg-brand text-[18px] font-bold text-on-brand"
                    >
                        {Array.from(displayName)[0] ?? ''}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <NameEditor displayName={displayName} />
                        <span className="truncate text-caption text-text-secondary">{user?.email}</span>
                    </div>
                </Card>

                <section className="flex flex-col gap-2">
                    <h2 className="m-0 text-section text-label-muted">내 사주 정보</h2>
                    <Card className="px-4 py-1">
                        {loadError ? (
                            <p role="alert" className="m-0 py-3 text-caption text-error">
                                {loadError}
                            </p>
                        ) : null}
                        {!loadError && sajuLoaded && !saju ? (
                            <p className="m-0 py-3 text-[15px] text-text-secondary">
                                아직 이모한테 생일을 안 알려 줬어요.
                            </p>
                        ) : null}
                        {!loadError && (saju || !sajuLoaded) ? (
                            <dl className="m-0">
                                <InfoRow
                                    label="생년월일"
                                    value={saju ? formatBirthDate(saju) : '불러오는 중'}
                                    bordered
                                />
                                <InfoRow label="태어난 시간" value={saju ? formatBirthTime(saju) : '불러오는 중'} />
                            </dl>
                        ) : null}
                        {saju ? (
                            <div className="flex min-h-[60px] items-center justify-between gap-3 border-t border-hairline py-2">
                                <span className="flex flex-col gap-0.5">
                                    <span className="text-[15px]">이모가 사주 얘기 곁들이기</span>
                                    <span className="text-caption text-text-secondary">
                                        꺼도 사주를 먼저 물으면 이모가 봐 줘요
                                    </span>
                                </span>
                                <Switch
                                    checked={sajuAside}
                                    label="이모가 사주 얘기 곁들이기"
                                    onChange={toggleSajuAside}
                                />
                            </div>
                        ) : null}
                    </Card>
                    {prefError ? (
                        <p role="alert" className="m-0 text-caption text-error">
                            {prefError}
                        </p>
                    ) : null}
                    <div className="flex items-center justify-end gap-1">
                        {saju ? <SajuDelete onDeleted={() => setSaju(null)} /> : null}
                        <Button
                            variant="text"
                            size="md"
                            className="px-3"
                            disabled={!sajuLoaded}
                            onClick={() => setBirthSheetOpen(true)}
                        >
                            {saju || !sajuLoaded ? '수정하기' : '생일 알려주기'}
                        </Button>
                    </div>
                </section>

                <section className="flex flex-col gap-2">
                    <h2 className="m-0 text-section text-label-muted">알림 받기</h2>
                    <Card className="px-4 py-0">
                        {notifications.map((item, index) => (
                            <div
                                key={item.kind}
                                className={`flex h-[60px] items-center justify-between ${
                                    index < notifications.length - 1 ? 'border-b border-hairline' : ''
                                }`}
                            >
                                <span className="text-[15px]">{item.name}</span>
                                <Switch
                                    checked={item.enabled}
                                    label={`${item.name} 알림`}
                                    onChange={(enabled) => toggleNotification(item.kind, enabled)}
                                />
                            </div>
                        ))}
                    </Card>
                    {notifyError ? (
                        <p role="alert" className="m-0 text-caption text-error">
                            {notifyError}
                        </p>
                    ) : null}
                </section>

                <section className="flex flex-col gap-2">
                    <h2 className="m-0 text-section text-label-muted">별채</h2>
                    <Card className="px-4 py-0">
                        <div className="flex min-h-[60px] items-center justify-between gap-3 py-2">
                            <span className="flex flex-col gap-0.5">
                                <span className="text-[15px]">내가 없을 때 이어지는 얘기</span>
                                <span className="text-caption text-text-secondary">
                                    내가 나간 뒤 멤버들이 내 얘기를 한두 마디 더 이어 가요
                                </span>
                            </span>
                            <Switch
                                checked={roomAfterglow}
                                label="내가 없을 때 이어지는 얘기"
                                onChange={toggleRoomAfterglow}
                            />
                        </div>
                        <div className="flex min-h-[60px] items-center justify-between gap-3 border-t border-hairline py-2">
                            <span className="flex flex-col gap-0.5">
                                <span className="text-[15px]">멤버가 먼저 이야기 꺼내기</span>
                                <span className="text-caption text-text-secondary">
                                    대화가 멈췄을 때 멤버가 화제를 하나 던져요
                                </span>
                            </span>
                            <Switch
                                checked={topicNudge}
                                label="멤버가 먼저 이야기 꺼내기"
                                onChange={toggleTopicNudge}
                            />
                        </div>
                    </Card>
                    {roomPrefError ? (
                        <p role="alert" className="m-0 text-caption text-error">
                            {roomPrefError}
                        </p>
                    ) : null}
                </section>

                <section className="flex flex-col gap-2">
                    <h2 className="m-0 text-section text-label-muted">멤버</h2>
                    <button
                        type="button"
                        onClick={() => navigate('/members', { state: { from: '/me' } })}
                        className="flex h-14 items-center gap-3 rounded-card border border-hairline bg-surface px-4 text-left"
                    >
                        <AvatarStack memberKeys={MEMBER_KEYS} size={24} />
                        <span className="flex-1 text-[15px] font-semibold">멤버 소개</span>
                        <ChevronRight size={18} strokeWidth={1.6} aria-hidden="true" className="text-text-secondary" />
                    </button>
                </section>

                <section className="flex flex-col gap-2">
                    <h2 className="m-0 text-section text-label-muted">이모 수첩</h2>
                    {/* 옛 '기억'. 멤버들이 들은 얘기를 보고 고치고 지우는 곳 (시안 04 D2c) */}
                    <button
                        type="button"
                        onClick={() => navigate('/me/memo')}
                        className="flex h-14 items-center gap-3 rounded-card border border-hairline bg-surface px-4 text-left"
                    >
                        <NotebookPen size={22} strokeWidth={1.6} aria-hidden="true" className="text-brand" />
                        <span className="flex-1 text-[15px] font-semibold">이모 수첩</span>
                        <ChevronRight size={18} strokeWidth={1.6} aria-hidden="true" className="text-text-secondary" />
                    </button>
                </section>

                <section className="flex flex-col gap-2">
                    <h2 className="m-0 text-section text-label-muted">약관 · AI 고지</h2>
                    {/* 이용약관 · 개인정보 처리방침 · AI 대화 이용 안내(DEV-07r 2 · D97) */}
                    <button
                        type="button"
                        onClick={() => navigate('/legal')}
                        className="flex h-14 items-center gap-3 rounded-card border border-hairline bg-surface px-4 text-left"
                    >
                        <FileText size={22} strokeWidth={1.6} aria-hidden="true" className="text-brand" />
                        <span className="flex-1 text-[15px] font-semibold">약관 · AI 고지</span>
                        <ChevronRight size={18} strokeWidth={1.6} aria-hidden="true" className="text-text-secondary" />
                    </button>
                </section>

                <AccountSection
                    account={account}
                    email={user?.email}
                    onLogout={handleLogout}
                    onDelete={() => navigate('/me/delete')}
                />

                <p className="m-0 text-center text-caption leading-5 text-text-secondary">
                    인생상회의 멤버들은 AI가 연기하는 가상 인물이에요. 운세는 재미로 봐 주세요.
                </p>
            </main>
            <BirthSheet
                open={birthSheetOpen}
                initialBirth={saju}
                title={saju ? '사주 정보 수정' : '이모한테 생일 알려주기'}
                onClose={() => setBirthSheetOpen(false)}
                onSaved={(saved) => {
                    setSaju(saved);
                    setBirthSheetOpen(false);
                }}
            />
        </>
    );
}

function InfoRow({ label, value, bordered = false }) {
    return (
        <div className={`flex min-h-12 items-center justify-between ${bordered ? 'border-b border-hairline' : ''}`}>
            <dt className="text-label font-normal text-text-secondary">{label}</dt>
            <dd className="m-0 text-[15px]">{value}</dd>
        </div>
    );
}
